import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { StatusChip } from '@mysaiva/core'
import styles from './Docs.module.scss'

export interface DocAnchor {
  id: string
  label: string
}

interface DocLayoutProps {
  /** DOM id of the article, the scroll target on continuous pages. */
  id?: string
  title: string
  /** Rendered next to the title (e.g. method + path). */
  titleExtra?: ReactNode
  draft?: boolean
  /** "On this page" links to the third-level items. */
  anchors?: DocAnchor[]
  main: ReactNode
  aside?: ReactNode
  'data-testid'?: string
}

/**
 * Two-column documentation layout: explanatory text left, code panels
 * right, stacked on narrow screens.
 */
export function DocLayout({
  id,
  title,
  titleExtra,
  draft,
  anchors,
  main,
  aside,
  'data-testid': testId = 'dp-doc'
}: DocLayoutProps) {
  const { t } = useTranslation()
  return (
    <article id={id} className={styles.doc} data-testid={testId} data-draft={draft || undefined}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
          {draft && (
            <StatusChip tone="warning" variant="filled" data-testid="dp-doc-draft">
              {t('developerPlatform.docs.ui.draft')}
            </StatusChip>
          )}
          {titleExtra}
        </div>
        {anchors && anchors.length > 0 && (
          <ul className={styles.onThisPage} aria-label={t('developerPlatform.docs.ui.onThisPage')}>
            <li>{t('developerPlatform.docs.ui.onThisPage')}:</li>
            {anchors.map(anchor => (
              <li key={anchor.id}>
                <a href={`#${anchor.id}`} data-testid={`dp-doc-anchor-${anchor.id}`}>
                  {anchor.label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </header>
      <div className={styles.columns}>
        <div className={styles.column}>{main}</div>
        {aside && <div className={`${styles.column} ${styles.aside}`}>{aside}</div>}
      </div>
    </article>
  )
}
